import React from 'react';
import SxProps from 'threads5/themes/sx-props';
import type { ITheme } from 'threads5/themes/theme';
import { Stack, Box } from 'src/components-v2/Layout';
import { BackgroundContext } from 'src/contexts';
import { CmsBackgroundColors } from 'src/lib/ts-types';
import { useIsDarkMode } from 'src/hooks/useIsDarkMode';
import { useComponentTheme } from 'src/hooks/useComponentTheme';
import ThemeProvider from 'threads5/themes/theme-provider';
import { useCmsTheme } from 'src/contexts/CmsThemeContext/CmsThemeContextProvider';
import { gridCardStyles } from './Card.styles';
import { CmsThemes } from 'src/lib/constants';

interface ICard {
  children: React.ReactNode;
  sx?: SxProps<ITheme>;
  mode?: string;
  backgroundColor?: CmsBackgroundColors;
  className?: string;
  showHolographicBorder?: boolean;
}

const getCardMode = ({ cmsTheme, mode, isDark }) => {
  if (cmsTheme === CmsThemes.WEB2) {
    return isDark ? 'dark' : 'light';
  }

  return mode || (isDark ? 'dark' : 'light');
};

const getProviderBgColor = ({ cardMode }) => {
  return cardMode === 'dark' ? 'black' : 'white';
};

const Card: React.FC<ICard> = ({
  children,
  sx,
  mode = '',
  backgroundColor = 'white',
  className = '',
  showHolographicBorder = true,
}) => {
  const cmsTheme = useCmsTheme();
  const isDark = useIsDarkMode();
  const cardMode = getCardMode({ cmsTheme, mode, isDark });
  const cardTheme = useComponentTheme(cardMode);
  const bgProviderColor = getProviderBgColor({ cardMode });

  return (
    <ThemeProvider theme={cardTheme}>
      <BackgroundContext.Provider value={bgProviderColor}>
        <Box
          className={className}
          data-testid='grid-card'
          data-holographic-border={showHolographicBorder ? 'true' : 'false'}
          sx={{
            height: '100%',
            padding: '3px',
            background: 'transparent',
            borderRadius: '20px',
            position: 'relative',
            // Pseudo-element for the holographic conic-gradient border.
            // Using a pseudo-element allows us to transition opacity on hover
            // instead of swapping background, which cannot be transitioned with gradients.
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              zIndex: 0,
              borderRadius: 'inherit',
              background: (theme) => {
                return `conic-gradient(from 180deg, ${theme.palette['base/pink/300']} 0%, ${theme.palette['base/red/300']} 15%, ${theme.palette['base/yellow/300']} 30%, ${theme.palette['base/lime/300']} 46%, ${theme.palette['base/sky/300']} 64%, ${theme.palette['base/purple/300']} 81%, ${theme.palette['base/pink/300']} 100%)`;
              },
              opacity: 0,
              transition: 'opacity 0.3s ease',
              // Reveal instantly under reduced motion. Happo renders with
              // `prefers-reduced-motion: reduce`, so this also pins the border
              // to a deterministic frame instead of a random point in the fade
              // (avoids flaky snapshot diffs in the gap between cards).
              '@media (prefers-reduced-motion: reduce)': {
                transition: 'none',
              },
              pointerEvents: 'none',
            },
            // Attribute-driven so hover styles stay in the sheet whenever the
            // card mounts; whole-card .btn ::after hovers the CTA, not the shell.
            '&[data-holographic-border="true"]:hover::before, &[data-holographic-border="true"]:has(.btn:hover)::before':
              {
                opacity: 1,
              },
          }}
        >
          <Stack
            direction='column'
            sx={{
              ...gridCardStyles({
                cmsTheme,
                backgroundColor: backgroundColor,
                isDark: isDark,
              }),
              position: 'relative',
              zIndex: 1,
              height: '100%',
              p: {
                xs: '20px',
                sm: '20px',
                lg: '32px 24px',
              },
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              textAlign: 'left',
              '& h3': {
                mb: '16px',
              },
              '& p': {
                mb: '32px',
              },
              '& .btn': {
                marginTop: 'auto',
              },
              '& img': {
                width: '48px',
                mb: '24px',
              },
              '& .featured-paragraph': {
                mb: { xs: '1.5rem', sm: '1.6rem', md: '2.4rem' },
              },
              '& p:nth-last-of-type(1):empty': {
                display: 'none',
              },
              ...sx,
            }}
          >
            {children}
          </Stack>
        </Box>
      </BackgroundContext.Provider>
    </ThemeProvider>
  );
};

export default Card;
